import {useCallback} from 'react'
import {reactFetch} from '@github-ui/verified-fetch'

import {
  SITE_ADMIN_LABEL,
  SITE_ADMIN_TURN_OFF_LABEL,
  SITE_ADMIN_TURN_ON_LABEL,
  STAFF_TOGGLES_NAV_LABEL,
  STATE_OFF_LABEL,
  STATE_ON_LABEL,
  UI_SERVICE_LABEL,
  UI_SERVICE_OPT_IN_LABEL,
  UI_SERVICE_OPT_OUT_LABEL,
} from '../../marketing-footer.data'

import styles from './StaffToggleBar.module.css'

type Props = {
  showAdminToggle: boolean
  isAdminModeOn: boolean
  adminTogglePath?: string
  showUiOptOut: boolean
  isUiOptedOut: boolean
  uiOptOutTogglePath?: string
}

export function StaffToggleBar(props: Props) {
  const {showAdminToggle, isAdminModeOn, adminTogglePath, showUiOptOut, isUiOptedOut, uiOptOutTogglePath} = props

  const renderAdmin = showAdminToggle && Boolean(adminTogglePath)
  const renderUiOptOut = showUiOptOut && Boolean(uiOptOutTogglePath)

  const handleAdminToggle = useCallback(async () => {
    if (!adminTogglePath) return
    try {
      await reactFetch(adminTogglePath, {method: 'PATCH'})
      window.location.reload()
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle site admin mode')
    }
  }, [adminTogglePath])

  const handleUiOptOutToggle = useCallback(async () => {
    if (!uiOptOutTogglePath) return
    try {
      await reactFetch(uiOptOutTogglePath, {method: 'PATCH'})
      window.location.reload()
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle UI service opt-out')
    }
  }, [uiOptOutTogglePath])

  if (!renderAdmin && !renderUiOptOut) {
    return null
  }

  return (
    <div className={styles.bar}>
      <div className={styles.container}>
        <nav aria-label={STAFF_TOGGLES_NAV_LABEL()}>
          <ul className={styles.list}>
            {renderAdmin && (
              <li>
                <button
                  type="button"
                  className={styles.button}
                  data-hotkey="`,Shift+S"
                  onClick={handleAdminToggle}
                  aria-pressed={isAdminModeOn}
                  aria-label={isAdminModeOn ? SITE_ADMIN_TURN_OFF_LABEL() : SITE_ADMIN_TURN_ON_LABEL()}
                  data-analytics-event={JSON.stringify({
                    location: 'footer',
                    action: 'toggle_site_admin_mode',
                    label: `state:${isAdminModeOn ? 'on' : 'off'}`,
                  })}
                >
                  {SITE_ADMIN_LABEL()}{' '}
                  <span className={styles.state}>{isAdminModeOn ? STATE_ON_LABEL() : STATE_OFF_LABEL()}</span>
                </button>
              </li>
            )}
            {renderUiOptOut && (
              <li>
                <button
                  type="button"
                  className={styles.button}
                  onClick={handleUiOptOutToggle}
                  aria-pressed={isUiOptedOut}
                  aria-label={isUiOptedOut ? UI_SERVICE_OPT_IN_LABEL() : UI_SERVICE_OPT_OUT_LABEL()}
                  data-analytics-event={JSON.stringify({
                    location: 'footer',
                    action: 'toggle_ui_service_opt_out',
                    label: `state:${isUiOptedOut ? 'opted_out' : 'opted_in'}`,
                  })}
                >
                  {UI_SERVICE_LABEL()}{' '}
                  <span className={styles.state}>{isUiOptedOut ? STATE_OFF_LABEL() : STATE_ON_LABEL()}</span>
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </div>
  )
}
